import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Plus, Trash2, Pencil, Check, X } from 'lucide-react';

const ZONE_OPTIONS = [
  { value: 'za', label: 'Zone A' },
  { value: 'zb', label: 'Zone B' },
  { value: 'zc', label: 'Zone C' },
  { value: 'remainder', label: 'Remainder' },
  { value: 'ancillary', label: 'Ancillary' },
  { value: 'basement', label: 'Basement' },
  { value: 'upper_parts', label: 'Upper Parts' },
];

const ZONE_WEIGHTS = { za: 1.0, zb: 0.5, zc: 0.25, remainder: 0.125, ancillary: 0.1, basement: 0.15, upper_parts: 0.1 };
const LOCATION_OPTIONS = ['internal', 'balcony', 'terrace', 'yard', 'garage', 'store'];

const emptyRow = {
  zone: 'za', frontage: '', depth: '', area: '', weighting: '',
  adjusted_area: '', notes: '', location_type: 'internal', included_in_valuation: true,
};

export default function ITZATable({ rows, onAdd, onUpdate, onDelete }) {
  const [newRow, setNewRow] = useState({ ...emptyRow });
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});

  const autoWeight = (zone) => ZONE_WEIGHTS[zone] || 1;

  const handleAdd = () => {
    const area = parseFloat(newRow.area) || 0;
    const weight = parseFloat(newRow.weighting) || autoWeight(newRow.zone);
    onAdd({
      ...newRow,
      frontage: parseFloat(newRow.frontage) || 0,
      depth: parseFloat(newRow.depth) || 0,
      area,
      weighting: weight,
      adjusted_area: parseFloat(newRow.adjusted_area) || round2(area * weight),
    });
    setNewRow({ ...emptyRow });
  };

  const startEdit = (row) => { setEditId(row.id); setEditData({ ...row }); };
  const saveEdit = () => {
    const area = parseFloat(editData.area) || 0;
    const weight = parseFloat(editData.weighting) || autoWeight(editData.zone);
    onUpdate(editId, {
      ...editData,
      frontage: parseFloat(editData.frontage) || 0,
      depth: parseFloat(editData.depth) || 0,
      area,
      weighting: weight,
      adjusted_area: parseFloat(editData.adjusted_area) || round2(area * weight),
    });
    setEditId(null);
  };

  const round2 = (n) => Math.round(n * 100) / 100;

  // Auto-calc on zone/area change for new row
  const updateNewArea = (field, value) => {
    const updated = { ...newRow, [field]: value };
    if (field === 'zone' && !newRow.weighting) {
      updated.weighting = autoWeight(value);
    }
    const a = parseFloat(updated.area) || 0;
    const w = parseFloat(updated.weighting) || autoWeight(updated.zone);
    if (a) updated.adjusted_area = round2(a * w);
    setNewRow(updated);
  };

  const totalITZA = rows.reduce((sum, r) => sum + (r.adjusted_area || 0), 0);

  return (
    <div className="space-y-3" data-testid="itza-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="text-left p-2 font-medium text-gray-600">Zone</th>
              <th className="text-right p-2 font-medium text-gray-600 w-24">Frontage (m)</th>
              <th className="text-right p-2 font-medium text-gray-600 w-24">Depth (m)</th>
              <th className="text-right p-2 font-medium text-gray-600 w-24">Area (m²)</th>
              <th className="text-right p-2 font-medium text-gray-600 w-24">Weighting</th>
              <th className="text-right p-2 font-medium text-gray-600 w-28">ITZA (m²)</th>
              <th className="text-left p-2 font-medium text-gray-600">Location</th>
              <th className="text-center p-2 font-medium text-gray-600 w-16">Valued</th>
              <th className="text-left p-2 font-medium text-gray-600">Notes</th>
              <th className="p-2 w-20"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.id} className="border-b hover:bg-gray-50/50" data-testid={`itza-row-${row.id}`}>
                {editId === row.id ? (
                  <>
                    <td className="p-1">
                      <Select value={editData.zone || 'za'} onValueChange={v => setEditData({...editData, zone: v})}>
                        <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{ZONE_OPTIONS.map(z => <SelectItem key={z.value} value={z.value}>{z.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="p-1"><Input className="h-7 text-xs text-right" type="number" step="0.01" value={editData.frontage || ''} onChange={e => setEditData({...editData, frontage: e.target.value})} /></td>
                    <td className="p-1"><Input className="h-7 text-xs text-right" type="number" step="0.01" value={editData.depth || ''} onChange={e => setEditData({...editData, depth: e.target.value})} /></td>
                    <td className="p-1"><Input className="h-7 text-xs text-right" type="number" step="0.01" value={editData.area || ''} onChange={e => setEditData({...editData, area: e.target.value})} /></td>
                    <td className="p-1"><Input className="h-7 text-xs text-right" type="number" step="0.001" value={editData.weighting || ''} onChange={e => setEditData({...editData, weighting: e.target.value})} /></td>
                    <td className="p-1"><Input className="h-7 text-xs text-right" type="number" step="0.01" value={editData.adjusted_area || ''} onChange={e => setEditData({...editData, adjusted_area: e.target.value})} /></td>
                    <td className="p-1">
                      <Select value={editData.location_type || 'internal'} onValueChange={v => setEditData({...editData, location_type: v})}>
                        <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{LOCATION_OPTIONS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="p-1 text-center"><input type="checkbox" checked={editData.included_in_valuation !== false} onChange={e => setEditData({...editData, included_in_valuation: e.target.checked})} /></td>
                    <td className="p-1"><Input className="h-7 text-xs" value={editData.notes || ''} onChange={e => setEditData({...editData, notes: e.target.value})} /></td>
                    <td className="p-1 flex gap-1">
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={saveEdit}><Check className="h-3 w-3 text-green-600" /></Button>
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => setEditId(null)}><X className="h-3 w-3" /></Button>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="p-2 font-medium">{ZONE_OPTIONS.find(z => z.value === row.zone)?.label || row.zone}</td>
                    <td className="p-2 text-right font-mono">{row.frontage || '-'}</td>
                    <td className="p-2 text-right font-mono">{row.depth || '-'}</td>
                    <td className="p-2 text-right font-mono">{row.area ? Number(row.area).toFixed(2) : '-'}</td>
                    <td className="p-2 text-right font-mono">{row.weighting || '-'}</td>
                    <td className="p-2 text-right font-mono font-medium">{row.adjusted_area ? Number(row.adjusted_area).toFixed(2) : '-'}</td>
                    <td className="p-2 text-gray-500 capitalize">{row.location_type || 'internal'}</td>
                    <td className="p-2 text-center">{row.included_in_valuation !== false ? '✓' : '—'}</td>
                    <td className="p-2 text-gray-400 text-xs truncate max-w-[100px]">{row.notes || '-'}</td>
                    <td className="p-1 flex gap-1">
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => startEdit(row)}><Pencil className="h-3 w-3" /></Button>
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => onDelete(row.id)}><Trash2 className="h-3 w-3 text-red-400" /></Button>
                    </td>
                  </>
                )}
              </tr>
            ))}
            {/* New row */}
            <tr className="border-b bg-blue-50/30">
              <td className="p-1">
                <Select value={newRow.zone} onValueChange={v => updateNewArea('zone', v)}>
                  <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{ZONE_OPTIONS.map(z => <SelectItem key={z.value} value={z.value}>{z.label}</SelectItem>)}</SelectContent>
                </Select>
              </td>
              <td className="p-1"><Input className="h-7 text-xs text-right" type="number" step="0.01" placeholder="F" value={newRow.frontage} onChange={e => setNewRow({...newRow, frontage: e.target.value})} /></td>
              <td className="p-1"><Input className="h-7 text-xs text-right" type="number" step="0.01" placeholder="D" value={newRow.depth} onChange={e => setNewRow({...newRow, depth: e.target.value})} /></td>
              <td className="p-1"><Input className="h-7 text-xs text-right" type="number" step="0.01" placeholder="Area" value={newRow.area} onChange={e => updateNewArea('area', e.target.value)} data-testid="new-itza-area" /></td>
              <td className="p-1"><Input className="h-7 text-xs text-right" type="number" step="0.001" placeholder="Wt" value={newRow.weighting} onChange={e => updateNewArea('weighting', e.target.value)} /></td>
              <td className="p-1"><Input className="h-7 text-xs text-right bg-gray-50" type="number" step="0.01" value={newRow.adjusted_area} readOnly tabIndex={-1} /></td>
              <td className="p-1">
                <Select value={newRow.location_type} onValueChange={v => setNewRow({...newRow, location_type: v})}>
                  <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{LOCATION_OPTIONS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </td>
              <td className="p-1 text-center"><input type="checkbox" checked={newRow.included_in_valuation !== false} onChange={e => setNewRow({...newRow, included_in_valuation: e.target.checked})} /></td>
              <td className="p-1"><Input className="h-7 text-xs" placeholder="Notes" value={newRow.notes} onChange={e => setNewRow({...newRow, notes: e.target.value})} /></td>
              <td className="p-1"><Button size="sm" className="h-7 w-7 p-0" onClick={handleAdd} data-testid="add-itza-btn"><Plus className="h-3.5 w-3.5" /></Button></td>
            </tr>
            {/* Total row */}
            {rows.length > 0 && (
              <tr className="bg-gray-100 font-medium">
                <td className="p-2" colSpan={5}>Total ITZA</td>
                <td className="p-2 text-right font-mono">{totalITZA.toFixed(2)}</td>
                <td colSpan={4}></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
